import type { NodeKind } from '@api/processes';
import type { NodeDisplayState } from '../EditorContext';

/**
 * AUTO-Gate iteration cap mirror — keeps the FE aware of the same
 * default the backend's
 * {@code com.aibuild.os.controlplane.process.definition.gate.GateConfig#DEFAULT_AUTO_ITERATIONS}
 * uses. Surfaces only when the persisted config doesn't carry the
 * value (legacy rows pre-date the field) so the in-canvas subtitle
 * still renders a sensible "Iteration: x/N" while the reviewer agent
 * runs.
 */
const DEFAULT_GATE_MAX_ITERATIONS = 5;

export interface ResolveGateRunSubtitleArgs {
  readonly kind: NodeKind;
  readonly mode: unknown;
  readonly runState: NodeDisplayState;
  readonly maxIterations: unknown;
}

/**
 * Coerce a raw config value (which can arrive as a number from the
 * persisted JSON or as a string from the SELECT control) to a clamped
 * integer in the supported {@code [1..10]} range, or fall back to the
 * default cap when the field is absent or otherwise non-parseable.
 * Mirror of the Java {@code GateConfig#effectiveMaxIterations} clamp
 * so the in-canvas subtitle and the backend runtime guard always
 * agree on the displayed denominator.
 */
function clampMaxIterations(raw: unknown): number {
  const parsed =
    typeof raw === 'number'
      ? raw
      : typeof raw === 'string'
        ? Number(raw)
        : Number.NaN;
  if (!Number.isFinite(parsed)) return DEFAULT_GATE_MAX_ITERATIONS;
  if (parsed < 1) return 1;
  if (parsed > 10) return 10;
  return Math.floor(parsed);
}

/**
 * Produce the AUTO-gate "Iteration: x/N" subtitle string whenever the
 * gate has been touched in the current SSE-tracked run, or
 * {@code undefined} for every other case.
 *
 * <p>Returns {@code undefined} (not the empty string) so the
 * {@link NodeCardShell} can keep its existing "no subtitle slot"
 * fast-path — the shell renders the dedicated
 * {@code HeaderSubtitle} element only when {@code subtitle} is
 * truthy, so an empty string would still occupy header height.</p>
 *
 * <p>Only AUTO mode shows the counter — MANUAL mode loops are driven
 * by humans and don't have a meaningful iteration bound. Non-gate
 * kinds never show the counter regardless of mode.</p>
 *
 * <p>The subtitle is shown during {@code running} AND afterwards
 * ({@code completed} / {@code failed}) for as long as the gate is
 * still in the live run map. AUTO gates typically execute in seconds
 * and the loop may swing back to the upstream node before the user
 * looks at the canvas; if we only rendered the subtitle during
 * {@code running}, the cap would visibly flash on screen and disappear
 * — which is the regression that prompted this fix. Once the page is
 * refreshed (no live map) the gate falls back to {@link source}
 * {@code 'persisted'} / {@code 'none'}, the live snapshot is
 * {@code undefined}, and we hide the subtitle so historical runs
 * don't keep advertising a stale iteration count.</p>
 *
 * <p>{@code skipped} is excluded — a skipped node never fired, so an
 * iteration counter for it is meaningless.</p>
 */
export function resolveGateRunSubtitle({
  kind,
  mode,
  runState,
  maxIterations,
}: ResolveGateRunSubtitleArgs): string | undefined {
  if (kind !== 'gate') return undefined;
  if (mode !== 'AUTO') return undefined;
  const live = runState.live;
  if (!live) return undefined;
  if (runState.status === 'idle' || runState.status === 'skipped') {
    return undefined;
  }
  const current = live.iteration + 1;
  const max = clampMaxIterations(maxIterations);
  return `Iteration ${current}/${max}`;
}
